import { useState, useRef, useCallback, useEffect } from 'react';
import { FaTimes, FaEnvelope, FaPhone, FaCheck } from 'react-icons/fa';
import logo from '../../assets/image/logo.png';

function PaymentPage({ isOpen, onClose, selectedPrice = 4000 }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [addOnSelected, setAddOnSelected] = useState(false);
  const paymentInitiated = useRef(false);
  
  const API_BASE_URL = process.env.REACT_APP_PAYMENT_API_URL || 'http://localhost:5005/api';
  const PAGE_URL = 'https://lp1.rushneeksingh.com/blocked-payments-back';
  
  const addOnPrice = 450;
  const productName = "Blocked Payments Recovery Kit";

  const calculateTotalAmount = useCallback(() => {
    return selectedPrice + (addOnSelected ? addOnPrice : 0);
  }, [selectedPrice, addOnSelected]);

  useEffect(() => {
    if (isOpen) {
      setFormData({ name: '', email: '', phone: '' });
      setErrors({});
      setAddOnSelected(false);
    }
  }, [isOpen]);

  const handleModalClose = useCallback(async () => {
    const hasContactInfo = formData.email.trim() || formData.phone.trim();
    
    if (hasContactInfo && !paymentInitiated.current) {
      try {
        await fetch(`${API_BASE_URL}/payments/pending`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name || 'Unknown',
            email: formData.email || '',
            phone: formData.phone || '',
            amount: calculateTotalAmount(),
            productName: productName,
            pageUrl: PAGE_URL
          })
        });
      } catch (err) {
        console.error('Failed to save pending payment:', err);
      }
    }
    setFormData({ name: '', email: '', phone: '' });
    setErrors({});
    paymentInitiated.current = false;
    onClose();
  }, [formData, onClose, API_BASE_URL, calculateTotalAmount]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    const isLoaded = await loadRazorpayScript();
    
    if (!isLoaded) {
      alert('Failed to load Razorpay. Please check your internet connection.');
      setIsLoading(false);
      return;
    }

    let paymentId = null;
    const totalAmount = calculateTotalAmount();
    
    try {
      const createResponse = await fetch(`${API_BASE_URL}/payments/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          amount: totalAmount,
          productName: productName
        })
      });
      const createData = await createResponse.json();
      if (createData.success) {
        paymentId = createData.paymentId;
        paymentInitiated.current = true;
      }
    } catch (err) {
      console.error('Failed to create payment record:', err);
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_live_fZxMHiP8A9K2bC7D',
      amount: totalAmount * 100,
      currency: 'INR',
      name: 'The Sales Coach India',
      description: productName,
      image: logo,
      prefill: {
        name: formData.name,
        email: formData.email,
        contact: formData.phone
      },
      notes: {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        product: productName,
        paymentId: paymentId
      },
      theme: {
        color: '#1e3a8a'
      },
      modal: {
        ondismiss: async function() {
          try {
            await fetch(`${API_BASE_URL}/payments/pending`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                paymentId,
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                amount: totalAmount,
                productName: productName,
                pageUrl: PAGE_URL
              })
            });
          } catch (err) {
            console.error('Failed to record pending payment:', err);
          }
          setIsLoading(false);
        }
      },
      handler: async function(response) {
        try {
          await fetch(`${API_BASE_URL}/payments/success`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              paymentId,
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              amount: totalAmount,
              productName: productName,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              payment_method: 'razorpay'
            })
          });
        } catch (err) {
          console.error('Failed to record successful payment:', err);
        }
        
        alert(`Payment Successful! 🎉\n\nPayment ID: ${response.razorpay_payment_id}\n\nThank you for your purchase!\n\n📧 You will receive login credentials at ${formData.email}`);
        setIsLoading(false);
        paymentInitiated.current = false;
        setFormData({ name: '', email: '', phone: '' });
        onClose();
      }
    };

    try {
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', async function(response) {
        try {
          await fetch(`${API_BASE_URL}/payments/failed`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              paymentId,
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              amount: totalAmount,
              productName: productName,
              error_code: response.error.code,
              error_description: response.error.description,
              pageUrl: PAGE_URL
            })
          });
        } catch (err) {
          console.error('Failed to record failed payment:', err);
        }
        
        alert(`Payment Failed!\n\nReason: ${response.error.description}`);
        setIsLoading(false);
      });
      razorpay.open();
    } catch (error) {
      console.error('Razorpay error:', error);
      alert('Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Blue gradient top bar */}
      <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600"></div>
      
      {/* Background */}
      <div 
        className="absolute inset-0 bg-gray-100"
        onClick={handleModalClose}
      ></div>

      {/* Close Button */}
      <button
        onClick={handleModalClose}
        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white shadow-lg text-gray-500 hover:text-gray-700 transition-all duration-200 z-20"
      >
        <FaTimes className="text-lg" />
      </button>

      {/* Main Container */}
      <div className="relative w-full max-w-5xl mx-4 max-h-[95vh] overflow-y-auto bg-white rounded-lg shadow-2xl">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left Side - Product Info */}
          <div className="bg-white p-8 lg:p-10 border-r border-gray-200">
            {/* Logo and Company */}
            <div className="flex items-center gap-3 mb-8">
              <img src={logo} alt="Logo" className="w-12 h-12 rounded-full object-contain bg-gray-100 p-1" />
              <span className="text-gray-800 font-semibold text-lg">The Sales Coach India</span>
            </div>

            {/* Title */}
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
              Get Your Blocked Payments Back"
            </h1>
            <div className="w-12 h-1 bg-blue-600 mb-6"></div>

            {/* Description */}
            <p className="text-blue-600 font-medium mb-6 leading-relaxed">
              "My Single Recovery Notice Has Helped Businesses Recover ₹10 Crores+ — Now It's Your Turn to Get Back What's Yours!"
            </p>

            {/* What You Get */}
            <div className="mb-6 space-y-3">
              {[
                'Safeguard Future Payments',
                'Get Payments From Defaulters Too',
                'Rock Solid Payment Recovery System',
                'No More Late Paying Customers',
                '1-2-1 Call - My Live Support (Weekly)'
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FaCheck className="text-green-600 text-[10px]" />
                  </div>
                  <span className="text-gray-700 font-medium text-sm">{item}</span>
                </div>
              ))}
            </div>

            {/* Product Image */}
            <div className="mb-8 rounded-lg overflow-hidden border border-gray-200">
              <img 
                src="https://lp1.rushneeksingh.com/static/media/blocked-payments-hero.jpg" 
                alt="Get Your Blocked Payments Back"
                className="w-full h-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-4">
                <h3 className="text-white font-bold text-lg">GET YOUR <span className="text-yellow-400">BLOCKED</span></h3>
                <h3 className="text-white font-bold text-lg">PAYMENTS BACK</h3>
                <p className="text-blue-200 text-sm mt-2">"Learn Proven Strategies to Recover your Stuck Payments Faster."</p>
              </div>
            </div>

            {/* Contact Info */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Contact Us:</h3>
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <FaEnvelope className="text-gray-400" />
                <span>rushneek@gmail.com</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <FaPhone className="text-gray-400" />
                <span>8920633120</span>
              </div>
            </div>

            {/* Terms */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-semibold text-gray-900 mb-2">Terms & Conditions:</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                You agree to share information entered on this page with The Sales Coach India (owner of this page) and Razorpay, adhering to applicable laws.
              </p>
            </div>

            {/* Razorpay Branding */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <span className="font-bold text-blue-600">₹</span>
                <span className="font-semibold">Razorpay</span>
              </div>
              <p className="text-gray-400 text-xs mt-1">
                Want to create page like this for your Business? Visit Razorpay Payment Pages to get started!
              </p>
            </div>
          </div>

          {/* Right Side - Payment Form */}
          <div className="bg-gray-50 p-8 lg:p-10">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Details</h2>
            <div className="w-12 h-1 bg-blue-600 mb-8"></div>

            <form onSubmit={handlePayment} className="space-y-6">
              {/* Amount */}
              <div>
                <label className="block text-gray-600 text-sm mb-2">Amount</label>
                <div className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 font-semibold">
                  ₹{selectedPrice.toLocaleString('en-IN')}.00
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-gray-600 text-sm mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                  className={`w-full bg-white border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20`}
                />
                {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-gray-600 text-sm mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit mobile number"
                  maxLength="10"
                  className={`w-full bg-white border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20`}
                />
                {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone}</p>}
              </div>

              {/* Name */}
              <div>
                <label className="block text-gray-600 text-sm mb-2">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className={`w-full bg-white border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20`}
                />
                {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
              </div>

              {/* Add-on Option */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <p className="text-gray-700 font-medium text-sm">18 New Advance Money Recovery Strategies Video for Recovery</p>
                    <p className="text-gray-500 text-xs mt-1">(Optional)</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-900 font-semibold">₹{addOnPrice}.00</p>
                    <label className="inline-flex items-center mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={addOnSelected}
                        onChange={(e) => setAddOnSelected(e.target.checked)}
                        className="w-5 h-5 border-2 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                  </div>
                </div>
                <p className="text-gray-500 text-xs mt-3 leading-relaxed">
                  "18 Powerful Methods" to recover stuck payments of Any Age - Any Amount - Any Client — Guaranteed Results.
                </p>
              </div>

              {/* Payment Icons */}
              <div className="flex items-center gap-3 py-4 border-t border-gray-200">
                <img src="https://cdn.razorpay.com/static/assets/pay_methods_branding/upi.png" alt="UPI" className="h-6" onError={(e) => e.target.style.display = 'none'} />
                <img src="https://cdn.razorpay.com/static/assets/pay_methods_branding/visa.png" alt="Visa" className="h-6" onError={(e) => e.target.style.display = 'none'} />
                <img src="https://cdn.razorpay.com/static/assets/pay_methods_branding/mastercard.png" alt="Mastercard" className="h-6" onError={(e) => e.target.style.display = 'none'} />
                <img src="https://cdn.razorpay.com/static/assets/pay_methods_branding/rupay.png" alt="RuPay" className="h-6" onError={(e) => e.target.style.display = 'none'} />
                <span className="text-gray-400 text-xs ml-auto">Secured by Razorpay</span>
              </div>

              {/* Pay Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-500 text-gray-900 py-4 rounded-lg text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>Pay ₹ {calculateTotalAmount().toLocaleString('en-IN')}.00</>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentPage;
